import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { StripeProvider } from './StripeProvider';
import { CheckoutForm } from './CheckoutForm';
import stripeService from '../../services/stripeService';
import { toast } from 'react-toastify';

/**
 * Payment Step Component
 *
 * Handles payment intent creation and displays the payment form.
 * This component orchestrates the entire payment flow.
 *
 * @param {Object} props
 * @param {Object} props.order - Order object with id and totalAmount
 * @param {Function} [props.onSuccess] - Success callback
 * @param {Function} [props.onError] - Error callback
 * @param {Function} [props.onBack] - Back button callback
 */
export function PaymentStep({ order, onSuccess, onError, onBack }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create payment intent when component mounts
  useEffect(() => {
    createPaymentIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  /**
   * Create payment intent for the order
   */
  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[PaymentStep] Creating payment intent for order:', order.id);

      const response = await stripeService.createPaymentIntent(order.id);

      console.log('[PaymentStep] Payment intent created:', response.paymentId);

      setClientSecret(response.clientSecret);
      setPaymentIntentId(response.paymentId);
      setLoading(false);
    } catch (err) {
      console.error('[PaymentStep] Failed to create payment intent:', err);
      setError(err.message || 'Failed to initialize payment');
      setLoading(false);

      toast.error('Failed to initialize payment. Please try again.');

      if (onError) {
        onError(err);
      }
    }
  };

  /**
   * Handle successful payment
   */
  const handlePaymentSuccess = () => {
    console.log('[PaymentStep] Payment successful!');
    toast.success('Payment successful! Confirming your order...');

    if (onSuccess) {
      onSuccess({ paymentIntentId, orderId: order.id });
    }
  };

  /**
   * Handle payment error
   */
  const handlePaymentError = (error, errorMessage) => {
    console.error('[PaymentStep] Payment error:', error);
    toast.error(errorMessage || 'Payment failed');

    if (onError) {
      onError(error);
    }
  };

  /**
   * Retry creating payment intent
   */
  const handleRetry = () => {
    createPaymentIntent();
  };

  // Loading state
  if (loading) {
    return (
      <div className="payment-step-loading">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4" />
          <p className="text-gray-600 text-lg">Initializing secure payment...</p>
          <p className="text-gray-500 text-sm mt-2">Please wait a moment</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="payment-step-error">
        <div className="max-w-md mx-auto text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <svg
              className="w-12 h-12 text-red-600 mx-auto mb-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z"
                clipRule="evenodd"
              />
            </svg>
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Unable to Initialize Payment
            </h3>
            <p className="text-red-700 mb-4">{error}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={handleRetry}
                className="px-6 py-2 bg-blue-600 text-gray-800 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              {onBack && (
                <button
                  onClick={onBack}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Go Back
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment form
  return (
    <div className="payment-step">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Information
            </h2>
            <p className="text-gray-600">
              Complete your payment securely using Stripe
            </p>
          </div>

          {/* Stripe Payment Form */}
          <StripeProvider clientSecret={clientSecret}>
            <CheckoutForm
              orderId={order.id}
              amount={order.totalAmount}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          </StripeProvider>

          {/* Back Button */}
          {onBack && (
            <div className="mt-6 text-center">
              <button
                onClick={onBack}
                className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center transition-colors"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Order Review
              </button>
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-4 text-center text-sm text-gray-500">
          <p>
            Your payment is processed securely by Stripe.
            <br />
            Your card details are never stored on our servers.
          </p>
        </div>
      </div>
    </div>
  );
}

PaymentStep.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.number.isRequired,
    totalAmount: PropTypes.number.isRequired,
  }).isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onBack: PropTypes.func,
};

export default PaymentStep;
